import { Dictionary, isUndefined, omitBy } from 'lodash';

import { TranslatableField } from '../api/menuEditor/gen/types';
import { BoltError } from '../types/types';
import { LocaleToLanguageMap, LocaleType } from './translations_types';

export function consoleLog(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.log(...args);
  }
}

export function consoleWarn(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.warn(...args);
  }
}

export function consoleError(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.error(...args);
  }
}

export function consoleJSON<T>(object: T) {
  consoleLog(JSON.stringify(object, null, 2));
}

export function devWarn(...args: unknown[]) {
  consoleLog('[DEV-WARN]', ...args);
}

export function generateLocaleLanguage(locales: LocaleType[]): { [key: string]: string } {
  const localeLanguage: Record<string, string> = {};
  locales.forEach((locale) => {
    localeLanguage[locale] = LocaleToLanguageMap[locale];
  });
  return localeLanguage;
}

export function isBoltError(error: unknown): error is BoltError {
  return (error as BoltError).code !== undefined;
}

export function parseAsBoltError(error?: any): BoltError | null {
  let errorData = error;
  if (errorData?.response) {
    errorData = errorData.response as BoltError;
  }
  return isBoltError(errorData) ? errorData : null;
}

export const objectsEqual = <T>(o1: T, o2: T): boolean => {
  return typeof o1 === 'object' && Object.keys(o1).length > 0
    ? Object.keys(o1).length === Object.keys(o2).length &&
        Object.keys(o1).every((p) => objectsEqual(o1[p as keyof T], o2[p as keyof T]))
    : o1 === o2;
};

export const clearUndefinedKeys = <T>(obj: T): Dictionary<any> => {
  return omitBy(obj, isUndefined);
};

export function translateField(
  field: TranslatableField,
  locale: LocaleType = LocaleType.English,
): string {
  if (field[locale] !== undefined) {
    return field[locale];
  }
  const anyField = Object.entries(field)[0];

  if (anyField) {
    devWarn(`No "${locale}" locale found, falling back "${anyField[0]}" - "${anyField[1]}`);
    return anyField[1];
  }

  const fieldJSON = JSON.stringify(field);
  devWarn(`Translation is empty, falling back to JSON "${fieldJSON}"`);
  return fieldJSON;
}
